import { getLocale } from '../i18n';

export const MINUTES_PER_DAY = 1440;
export const MAX_ABSOLUTE_MINUTE = MINUTES_PER_DAY * 2;
export const DEFAULT_COVERAGE_START_MINUTE = 0;
export const DEFAULT_COVERAGE_END_MINUTE = MINUTES_PER_DAY;
export const DEFAULT_SITE_TIMEZONE = 'UTC';
/** sites_required_guard_count_check allows 1 to 50 guards on site at once. */
export const MAX_REQUIRED_GUARDS = 50;

export interface CoverageWindow {
  startMinute: number;
  endMinute: number;
}

export interface SiteSchedulingLike {
  country_code?: string | null;
  timezone?: string | null;
  required_coverage_start_minute?: number | null;
  required_coverage_end_minute?: number | null;
}

export interface TimeZoneOption {
  value: string;
  label: string;
}

const COUNTRY_TIMEZONE_DEFAULTS: Record<string, string> = {
  AE: 'Asia/Dubai',
  AF: 'Asia/Kabul',
  AL: 'Europe/Tirane',
  AR: 'America/Argentina/Buenos_Aires',
  AT: 'Europe/Vienna',
  AU: 'Australia/Sydney',
  BE: 'Europe/Brussels',
  BG: 'Europe/Sofia',
  BR: 'America/Sao_Paulo',
  CA: 'America/Toronto',
  CH: 'Europe/Zurich',
  CL: 'America/Santiago',
  CN: 'Asia/Shanghai',
  CO: 'America/Bogota',
  CY: 'Asia/Nicosia',
  CZ: 'Europe/Prague',
  DE: 'Europe/Berlin',
  DK: 'Europe/Copenhagen',
  DZ: 'Africa/Algiers',
  EE: 'Europe/Tallinn',
  EG: 'Africa/Cairo',
  ES: 'Europe/Madrid',
  FI: 'Europe/Helsinki',
  FR: 'Europe/Paris',
  GB: 'Europe/London',
  GR: 'Europe/Athens',
  HK: 'Asia/Hong_Kong',
  HR: 'Europe/Zagreb',
  HU: 'Europe/Budapest',
  ID: 'Asia/Jakarta',
  IE: 'Europe/Dublin',
  IL: 'Asia/Jerusalem',
  IN: 'Asia/Kolkata',
  IT: 'Europe/Rome',
  JO: 'Asia/Amman',
  JP: 'Asia/Tokyo',
  KE: 'Africa/Nairobi',
  KR: 'Asia/Seoul',
  LT: 'Europe/Vilnius',
  LU: 'Europe/Luxembourg',
  LV: 'Europe/Riga',
  MT: 'Europe/Malta',
  MX: 'America/Mexico_City',
  MY: 'Asia/Kuala_Lumpur',
  NG: 'Africa/Lagos',
  NL: 'Europe/Amsterdam',
  NO: 'Europe/Oslo',
  NZ: 'Pacific/Auckland',
  PH: 'Asia/Manila',
  PK: 'Asia/Karachi',
  PL: 'Europe/Warsaw',
  PT: 'Europe/Lisbon',
  RO: 'Europe/Bucharest',
  RU: 'Europe/Moscow',
  SA: 'Asia/Riyadh',
  SE: 'Europe/Stockholm',
  SG: 'Asia/Singapore',
  SI: 'Europe/Ljubljana',
  SK: 'Europe/Bratislava',
  TH: 'Asia/Bangkok',
  TR: 'Europe/Istanbul',
  TW: 'Asia/Taipei',
  UA: 'Europe/Kyiv',
  US: 'America/New_York',
  VN: 'Asia/Ho_Chi_Minh',
  ZA: 'Africa/Johannesburg',
};

const MULTI_TIME_ZONE_COUNTRY_OPTIONS: Record<string, TimeZoneOption[]> = {
  AU: [
    { value: 'Australia/Perth', label: 'Western' },
    { value: 'Australia/Adelaide', label: 'Central' },
    { value: 'Australia/Sydney', label: 'Eastern' },
  ],
  BR: [
    { value: 'America/Sao_Paulo', label: 'Brasilia / Southeast' },
    { value: 'America/Manaus', label: 'Amazon' },
    { value: 'America/Rio_Branco', label: 'Acre' },
  ],
  CA: [
    { value: 'America/Halifax', label: 'Atlantic' },
    { value: 'America/Toronto', label: 'Eastern' },
    { value: 'America/Winnipeg', label: 'Central' },
    { value: 'America/Edmonton', label: 'Mountain' },
    { value: 'America/Vancouver', label: 'Pacific' },
  ],
  ES: [
    { value: 'Europe/Madrid', label: 'Mainland' },
    { value: 'Atlantic/Canary', label: 'Canary Islands' },
  ],
  ID: [
    { value: 'Asia/Jakarta', label: 'Western' },
    { value: 'Asia/Makassar', label: 'Central' },
    { value: 'Asia/Jayapura', label: 'Eastern' },
  ],
  MX: [
    { value: 'America/Cancun', label: 'Southeast' },
    { value: 'America/Mexico_City', label: 'Central' },
    { value: 'America/Chihuahua', label: 'Mountain' },
    { value: 'America/Tijuana', label: 'Pacific' },
  ],
  NZ: [
    { value: 'Pacific/Auckland', label: 'Mainland' },
    { value: 'Pacific/Chatham', label: 'Chatham Islands' },
  ],
  PT: [
    { value: 'Europe/Lisbon', label: 'Mainland / Madeira' },
    { value: 'Atlantic/Azores', label: 'Azores' },
  ],
  RU: [
    { value: 'Europe/Moscow', label: 'Moscow' },
    { value: 'Asia/Yekaterinburg', label: 'Yekaterinburg' },
    { value: 'Asia/Novosibirsk', label: 'Novosibirsk' },
    { value: 'Asia/Irkutsk', label: 'Irkutsk' },
    { value: 'Asia/Vladivostok', label: 'Vladivostok' },
  ],
  US: [
    { value: 'America/New_York', label: 'Eastern' },
    { value: 'America/Chicago', label: 'Central' },
    { value: 'America/Denver', label: 'Mountain' },
    { value: 'America/Los_Angeles', label: 'Pacific' },
    { value: 'America/Anchorage', label: 'Alaska' },
    { value: 'Pacific/Honolulu', label: 'Hawaii' },
  ],
};

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>();

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const parseMinuteNumber = (value: unknown, fallback: number): number => {
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getDateFormatter = (timeZone: string): Intl.DateTimeFormat => {
  if (!dateFormatterCache.has(timeZone)) {
    dateFormatterCache.set(
      timeZone,
      new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    );
  }

  return dateFormatterCache.get(timeZone)!;
};

const isValidTimeZone = (value: string): boolean => {
  try {
    new Intl.DateTimeFormat(getLocale(), { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

const splitDateKey = (dateKey: string): [number, number, number] => {
  const [year, month, day] = dateKey.split('-').map((part) => Number.parseInt(part, 10));
  return [year, month, day];
};

const toDateKeyFromUtcDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const normaliseCoverageWindow = (
  startValue: unknown = DEFAULT_COVERAGE_START_MINUTE,
  endValue: unknown = DEFAULT_COVERAGE_END_MINUTE,
): CoverageWindow => {
  const startMinute = clamp(
    parseMinuteNumber(startValue, DEFAULT_COVERAGE_START_MINUTE),
    0,
    MINUTES_PER_DAY - 1,
  );

  let endMinute = clamp(
    parseMinuteNumber(endValue, DEFAULT_COVERAGE_END_MINUTE),
    0,
    MAX_ABSOLUTE_MINUTE,
  );

  if (endMinute <= startMinute) endMinute += MINUTES_PER_DAY;
  if ((endMinute - startMinute) > MINUTES_PER_DAY) {
    endMinute = startMinute + MINUTES_PER_DAY;
  }

  return {
    startMinute,
    endMinute: clamp(endMinute, startMinute + 1, MAX_ABSOLUTE_MINUTE),
  };
};

export const getSiteCoverageWindow = (site?: SiteSchedulingLike | null): CoverageWindow => (
  normaliseCoverageWindow(
    site?.required_coverage_start_minute,
    site?.required_coverage_end_minute,
  )
);

export const getCoverageDurationMinutes = (coverageWindow: CoverageWindow): number => (
  coverageWindow.endMinute - coverageWindow.startMinute
);

export const formatAbsoluteMinute = (
  absoluteMinute: number,
  options?: { showMidnightAs24?: boolean },
): string => {
  const safeMinute = clamp(Math.round(absoluteMinute), 0, MAX_ABSOLUTE_MINUTE);
  const normalised = ((safeMinute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

  if (normalised === 0 && safeMinute > 0 && options?.showMidnightAs24) {
    return '24:00';
  }

  const hours = Math.floor(normalised / 60);
  const minutes = normalised % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const formatCoverageWindow = (site?: SiteSchedulingLike | null): string => {
  const coverageWindow = getSiteCoverageWindow(site);
  if (getCoverageDurationMinutes(coverageWindow) >= MINUTES_PER_DAY) {
    return '24 hours';
  }

  return `${formatAbsoluteMinute(coverageWindow.startMinute)} - ${formatAbsoluteMinute(coverageWindow.endMinute)}`;
};

/** Label for a time <select> option; the end-of-day slot reads as 24:00, not 00:00. */
export const formatTimeOptionLabel = (minute: number): string => (
  formatAbsoluteMinute(minute, { showMidnightAs24: minute === MINUTES_PER_DAY })
);

export const parseClockTimeInput = (value: string): number | null => {
  const parts = value.trim().split(':');
  if (parts.length !== 2) return null;

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);

  if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes < 0 || minutes > 59) {
    return null;
  }

  if (hours === 24 && minutes === 0) return MINUTES_PER_DAY;
  if (hours < 0 || hours > 23) return null;

  return (hours * 60) + minutes;
};

export const resolveClockMinuteToAbsoluteMinute = (
  minuteOfDay: number,
  coverageWindow: CoverageWindow,
): number | null => {
  const candidates = [minuteOfDay, minuteOfDay + MINUTES_PER_DAY];
  return candidates.find((candidate) => (
    candidate >= coverageWindow.startMinute && candidate <= coverageWindow.endMinute
  )) ?? null;
};

export const resolveTimeInputToAbsoluteMinute = (
  value: string,
  coverageWindow: CoverageWindow,
): number | null => {
  const minuteOfDay = parseClockTimeInput(value);
  if (minuteOfDay === null) return null;
  return resolveClockMinuteToAbsoluteMinute(minuteOfDay, coverageWindow);
};

export const getBrowserTimeZone = (): string => {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidTimeZone(browserZone) ? browserZone : DEFAULT_SITE_TIMEZONE;
};

export const getDefaultTimeZoneForCountry = (countryCode?: string | null): string => {
  if (!countryCode) return getBrowserTimeZone();
  return COUNTRY_TIMEZONE_DEFAULTS[countryCode.trim().toUpperCase()] ?? getBrowserTimeZone();
};

export const getTimeZoneOptionsForCountry = (countryCode?: string | null): TimeZoneOption[] => (
  MULTI_TIME_ZONE_COUNTRY_OPTIONS[countryCode?.trim().toUpperCase() ?? ''] ?? []
);

export const countryRequiresTimeZoneSelection = (countryCode?: string | null): boolean => (
  getTimeZoneOptionsForCountry(countryCode).length > 1
);

export const resolveTimeZoneForCountrySelection = (
  countryCode?: string | null,
  selectedTimeZone?: string | null,
): string => {
  const options = getTimeZoneOptionsForCountry(countryCode);
  const explicitTimeZone = selectedTimeZone?.trim();

  if (options.length > 1) {
    if (explicitTimeZone && options.some((option) => option.value === explicitTimeZone)) {
      return explicitTimeZone;
    }

    const defaultTimeZone = getDefaultTimeZoneForCountry(countryCode);
    if (options.some((option) => option.value === defaultTimeZone)) {
      return defaultTimeZone;
    }

    return options[0].value;
  }

  return getDefaultTimeZoneForCountry(countryCode);
};

export const resolveSiteTimeZone = (site?: SiteSchedulingLike | null): string => {
  const explicitTimeZone = site?.timezone?.trim();
  if (explicitTimeZone && isValidTimeZone(explicitTimeZone)) return explicitTimeZone;
  return getDefaultTimeZoneForCountry(site?.country_code);
};

export const getDateKeyInTimeZone = (date: Date, timeZone: string): string => {
  const parts = getDateFormatter(timeZone).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) {
    return toDateKeyFromUtcDate(new Date(Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
    )));
  }

  return `${year}-${month}-${day}`;
};

export const getCurrentDateKeyInTimeZone = (timeZone: string): string => (
  getDateKeyInTimeZone(new Date(), timeZone)
);

export const shiftDateKey = (dateKey: string, deltaDays: number): string => {
  const [year, month, day] = splitDateKey(dateKey);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + deltaDays);
  return toDateKeyFromUtcDate(date);
};

export const formatDateKeyLabel = (dateKey: string): string => {
  const [year, month, day] = splitDateKey(dateKey);
  const date = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat(getLocale(), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

export const buildCoverageAxisTicks = (coverageWindow: CoverageWindow): number[] => {
  const duration = getCoverageDurationMinutes(coverageWindow);
  const step = duration <= 360
    ? 60
    : duration <= 900
      ? 120
      : 180;

  const ticks: number[] = [];
  for (let minute = coverageWindow.startMinute; minute < coverageWindow.endMinute; minute += step) {
    ticks.push(minute);
  }

  if (ticks[ticks.length - 1] !== coverageWindow.endMinute) {
    ticks.push(coverageWindow.endMinute);
  }

  return ticks;
};

export const TIME_SELECT_OPTIONS = Array.from({ length: 96 }, (_, index) => index * 15);

/** Coverage end times additionally allow the closing 24:00 boundary. */
export const COVERAGE_END_OPTIONS = [...TIME_SELECT_OPTIONS, MINUTES_PER_DAY];

export const getWeekDates = (dateKey: string): string[] => {
  const [year, month, day] = splitDateKey(dateKey);
  const date = new Date(Date.UTC(year, month - 1, day));
  const dow = date.getUTCDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(date);
  monday.setUTCDate(monday.getUTCDate() + mondayOffset);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setUTCDate(d.getUTCDate() + i);
    return toDateKeyFromUtcDate(d);
  });
};

export const formatWeekRange = (weekDates: string[]): string => {
  if (weekDates.length === 0) return '';
  const [y1, m1, d1] = splitDateKey(weekDates[0]);
  const [y2, m2, d2] = splitDateKey(weekDates[weekDates.length - 1]);
  const date1 = new Date(Date.UTC(y1, m1 - 1, d1));
  const date2 = new Date(Date.UTC(y2, m2 - 1, d2));
  const fmt1 = new Intl.DateTimeFormat(getLocale(), { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const fmt2 = new Intl.DateTimeFormat(getLocale(), { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return `${fmt1.format(date1)} — ${fmt2.format(date2)}`;
};

export const formatDayOfWeek = (dateKey: string): string => {
  const [year, month, day] = splitDateKey(dateKey);
  const date = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat(getLocale(), { weekday: 'short', timeZone: 'UTC' }).format(date);
};

export const formatDayNumber = (dateKey: string): string => {
  const [, , day] = splitDateKey(dateKey);
  return String(day);
};
